/** Helpers for the /link device-code approval page. */

const DEVICE_CODE_LENGTH = 8;

/**
 * Normalizes free-form device-code input into the canonical `XXXX-XXXX`
 * display form: uppercase, hyphens/whitespace stripped before re-inserting
 * a single hyphen after the 4th character, truncated to 8 code characters.
 * Accepts lowercase input and input with or without the hyphen already
 * present.
 */
export function normalizeDeviceCodeInput(raw: string): string {
  const stripped = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, DEVICE_CODE_LENGTH);
  if (stripped.length <= 4) {
    return stripped;
  }
  return `${stripped.slice(0, 4)}-${stripped.slice(4)}`;
}

/** True once normalized input has all 8 code characters (`XXXX-XXXX`). */
export function isCompleteDeviceCode(normalized: string): boolean {
  return normalized.replace(/-/g, "").length === DEVICE_CODE_LENGTH;
}

const RELATIVE_UNITS: Array<{ limitSeconds: number; divisor: number; unit: string }> = [
  { limitSeconds: 60, divisor: 1, unit: "second" },
  { limitSeconds: 3600, divisor: 60, unit: "minute" },
  { limitSeconds: 86400, divisor: 3600, unit: "hour" },
];

function pluralize(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? "" : "s"}`;
}

/** Formats an ISO timestamp as "N seconds/minutes/hours ago" relative to `now`. */
export function formatRequestedAgo(requestedAt: string, now: Date = new Date()): string {
  const requested = new Date(requestedAt).getTime();
  if (Number.isNaN(requested)) {
    return "";
  }
  const diffSeconds = Math.max(0, Math.round((now.getTime() - requested) / 1000));
  if (diffSeconds < 5) {
    return "just now";
  }
  for (const { limitSeconds, divisor, unit } of RELATIVE_UNITS) {
    if (diffSeconds < limitSeconds) {
      return `${pluralize(Math.floor(diffSeconds / divisor), unit)} ago`;
    }
  }
  return "a while ago";
}

/** True once `expiresAt` has passed relative to `now`. */
export function isDeviceCodeExpired(expiresAt: string, now: Date = new Date()): boolean {
  const expires = new Date(expiresAt).getTime();
  if (Number.isNaN(expires)) {
    return false;
  }
  return expires <= now.getTime();
}

/** Formats an ISO expiry timestamp as "in N minutes" (or "expired") relative to `now`. */
export function formatExpiresIn(expiresAt: string, now: Date = new Date()): string {
  const expires = new Date(expiresAt).getTime();
  if (Number.isNaN(expires)) {
    return "";
  }
  const diffSeconds = Math.round((expires - now.getTime()) / 1000);
  if (diffSeconds <= 0) {
    return "expired";
  }
  if (diffSeconds < 60) {
    return `in ${pluralize(diffSeconds, "second")}`;
  }
  if (diffSeconds < 3600) {
    return `in ${pluralize(Math.round(diffSeconds / 60), "minute")}`;
  }
  return `in ${pluralize(Math.round(diffSeconds / 3600), "hour")}`;
}
